import { Row, Col, Form, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { I18nKey } from '../../../i18n/i18n.key';
import { useSSNavigate } from '../../../navigation/ss-navigate.hook';
import { Section, Question } from '../../../response/test.response';
import { QuestionType } from '../../../enum/question-type.enum';
import { useApp } from '../../../hook/app.hook';
import { useEffect } from 'react';
import { getLanguageContent } from '../../../util/language.util';
import { enterFullscreen } from '../../../full-screen/full-screen.hook';

const GoComponent = () => {
  const { testRes, language, updateLanguage, inProgress } = useApp();
  const { openTest } = useSSNavigate();
  const { t } = useTranslation();

  // Get available languages from test, default to ["en"] if empty
  const availableLanguages =
    testRes.languages && testRes.languages.length > 0
      ? testRes.languages
      : ['en'];

  // Set initial language to first available language if current language is not in available languages
  useEffect(() => {
    updateLanguage('en');
  }, []);

  const startTest = () => {
    // Must run synchronously inside the tap handler — mobile browsers block
    // requestFullscreen() when it is triggered from useEffect after navigation.
    try {
      enterFullscreen();
    } catch {
      // Test still opens if fullscreen is denied.
    }
    openTest();
  };

  // Helper function to get question type display name
  const getQuestionTypeDisplayName = (type: QuestionType): string => {
    switch (type) {
      case QuestionType.SINGLE_CHOICE:
        return 'Single Choice';
      case QuestionType.MULTIPLE_CHOICE:
        return 'Multiple Choice';
      case QuestionType.INT_ANS:
        return 'Numeric Type';
      case QuestionType.RANGE_ANS:
        return 'Range Answer';
      case QuestionType.MATRIX_MATCH:
        return 'Matrix Match';
      case QuestionType.LONG_ANSWER:
        return 'Long Answer';
      default:
        return type;
    }
  };

  // Helper function to group questions by type for a section
  const getQuestionTypeSummary = (questions: Question[]) => {
    const summary: {
      [key: string]: {
        count: number;
        totalPositive: number;
        totalNegative: number;
      };
    } = {};

    questions.forEach((question) => {
      const typeKey = question.type;
      if (!summary[typeKey]) {
        summary[typeKey] = { count: 0, totalPositive: 0, totalNegative: 0 };
      }
      summary[typeKey].count += 1;
      summary[typeKey].totalPositive += question.marking.positive;
      summary[typeKey].totalNegative += question.marking.negative;
    });

    return summary;
  };

  // Helper function to get language display name
  const getLanguageDisplayName = (langCode: string): string => {
    switch (langCode) {
      case 'en':
        return 'English';
      case 'hi':
        return 'हिंदी (Hindi)';
      case 'gu':
        return 'ગુજરાતી (Gujarati)';
      default:
        return langCode.toUpperCase();
    }
  };

  return (
    <div className="d-flex justify-content-center flex-column">
      {/* Language Selection */}
      {availableLanguages.length > 0 && (
        <div className="mb-4 p-3 border border-primary rounded-3 bg-light">
          <h6 className="text-dark font-weight-600 mb-3">
            {t(I18nKey.SELECT_LANGUAGE_TEXT)}
          </h6>
          <div className="d-flex flex-wrap gap-2">
            {availableLanguages.map((langCode: string) => (
              <Button
                key={langCode}
                variant={language === langCode ? 'primary' : 'outline-primary'}
                size="sm"
                onClick={() => updateLanguage(langCode)}
                className="px-3 py-2"
              >
                {getLanguageDisplayName(langCode)}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* <h5 className="ft-16 font-weight-500">
        {t(I18nKey.READY_START_IN_TEXT)}
        <span className="ms-1 font-weight-700">1 min 35 sec</span>
      </h5>
      <div className="text-dimgray">
        {t(I18nKey.SELECT_SECTION_ATTEMPT_START_TEXT)}
      </div> */}
      <div className="border border-success rounded-25 py-3 px-lg-5 px-3 my-4">
        <Row className="border-bottom border-lightgray text-black pb-2">
          <Col md={4} xs={4} className="text-start text-uppercase px-lg-4 px-2">
            SR. NO.
          </Col>
          <Col
            md={4}
            xs={4}
            className="text-center text-uppercase px-lg-4 px-2"
          >
            QUESTION TYPE
          </Col>
          <Col md={4} xs={4} className="text-end text-uppercase px-lg-4 px-2">
            <span className="d-lg-block d-none">
              {t(I18nKey.NUMBER_OF_QUESTION_TEXT)}
            </span>
            <span className="d-lg-none d-block">{t(I18nKey.QUES)}</span>
          </Col>
        </Row>
        <div className="py-2">
          {testRes.sectionList.map((section: Section, index: number) => {
            const questionTypeSummary = getQuestionTypeSummary(
              section.questionList,
            );

            return (
              <div
                key={`section-wrapper-${section.id}_${index}`}
                className="mb-4 border rounded-3 bg-light p-3"
              >
                {/* Section Header */}
                <div className="mb-2 pb-2 border-bottom border-secondary">
                  <h6 className="text-dark font-weight-700 mb-0">
                    {getLanguageContent(section.name, language)}
                  </h6>
                </div>

                {Object.entries(questionTypeSummary).map(
                  ([questionType, summary], typeIndex: number) => {
                    const isLastItem =
                      typeIndex ===
                      Object.entries(questionTypeSummary).length - 1;
                    return (
                      <Row
                        key={`section-${section.id}-${questionType}`}
                        id={`section-option-${section.id}_${index}_${typeIndex}`}
                        className={`py-2 text-dimgray ${
                          !isLastItem ? 'border-bottom' : ''
                        }`}
                      >
                        <Col
                          md={4}
                          xs={4}
                          className="text-start px-2 font-weight-600"
                        >
                          <span className="text-muted small">
                            #{typeIndex + 1}
                          </span>
                        </Col>
                        <Col md={4} xs={4} className="text-center px-lg-4 px-2">
                          <span className="text-dark">
                            {getQuestionTypeDisplayName(
                              questionType as QuestionType,
                            )}
                          </span>
                        </Col>
                        <Col
                          md={4}
                          xs={4}
                          className="text-end px-lg-4 px-2 font-weight-600"
                        >
                          <span className="text-dark">{summary.count}</span>
                        </Col>
                      </Row>
                    );
                  },
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* <div className="text-dimgray small mb-2">
        {t(I18nKey.UNTIMED_SECTIONS_TIME_LIMIT_INFO)}
      </div> */}

      <div className="d-flex">
        <Button
          variant="primary"
          type="submit"
          className="rounded-25 px-lg-4 mt-lg-3 mt-2 font-weight-600 mx-lg-0 mx-1"
          onClick={() => {
            startTest();
          }}
        >
          {inProgress ? t(I18nKey.RESUME_TEXT) : t(I18nKey.START_TEXT)}
        </Button>
      </div>
    </div>
  );
};

export default GoComponent;
